import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SupadminService, UpgradePremiumResponse } from '../../_services/supadmin.service';
import { CommonModule } from '@angular/common';

// ─── Custom Validator ─────────────────────────────────────────────────────────

function phoneValidator(control: AbstractControl): { [key: string]: boolean } | null {
  const value = control.value?.replace(/\s/g, '');
  if (!value) return null;
  const phoneRegex = /^(\+\d{1,3})?\d{7,15}$/;
  return phoneRegex.test(value) ? null : { invalidPhone: true };
}

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-super-admin',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './super-admin.component.html',
  styleUrl: './super-admin.component.scss',
})
export class SuperAdminComponent implements OnInit, OnDestroy {
  premiumForm!: FormGroup;
  isSubmitting = false;
  submitError = '';

  /** Structured result from API — null until first successful submit */
  result: UpgradePremiumResponse | null = null;

  private destroy$ = new Subject<void>();

  keyPoints = [
    {
      icon: 'shield',
      role: 'Super Admin',
      description:
        'Full platform access — can grant, revoke, and manage premium status for any user.',
    },
    {
      icon: 'code',
      role: 'Project Developer',
      description:
        'Technical lead access — unlocks advanced API limits, debug tools, and preview features.',
    },
    {
      icon: 'star',
      role: 'Product Owner',
      description:
        'Business lead access — enables priority support, analytics dashboards, and early releases.',
    },
  ];

  constructor(
    private fb: FormBuilder,
    private superAdminService: SupadminService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Form Builder ─────────────────────────────────────────────────────────

  buildForm(): void {
    this.premiumForm = this.fb.group({
      phoneNumbers: this.fb.array([this.createPhoneControl()]),
    });
  }

  createPhoneControl(): FormGroup {
    return this.fb.group({
      phone: ['', [Validators.required, phoneValidator]],
    });
  }

  get phoneNumbers(): FormArray {
    return this.premiumForm.get('phoneNumbers') as FormArray;
  }

  // ─── Dynamic Field Handlers ───────────────────────────────────────────────

  addPhone(): void {
    if (this.phoneNumbers.length < 10) {
      this.phoneNumbers.push(this.createPhoneControl());
    }
  }

  removePhone(index: number): void {
    if (this.phoneNumbers.length > 1) {
      this.phoneNumbers.removeAt(index);
    }
  }

  // ─── Validation Helpers ───────────────────────────────────────────────────

  getPhoneControl(index: number): AbstractControl {
    return this.phoneNumbers.at(index).get('phone')!;
  }

  isPhoneInvalid(index: number): boolean {
    const ctrl = this.getPhoneControl(index);
    return ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }

  getPhoneError(index: number): string {
    const ctrl = this.getPhoneControl(index);
    if (ctrl.hasError('required')) return 'Phone number is required.';
    if (ctrl.hasError('invalidPhone'))
      return 'Enter a valid phone number (e.g. +91 9876543210).';
    return '';
  }

  hasDuplicates(): boolean {
    const values = this.phoneNumbers.controls
      .map((c) => c.get('phone')?.value?.replace(/\s/g, ''))
      .filter(Boolean);
    return values.length !== new Set(values).size;
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.premiumForm.invalid || this.hasDuplicates()) {
      this.premiumForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.submitError = '';
    this.result = null;

    const phones: string[] = this.phoneNumbers.controls.map((c) =>
      c.get('phone')!.value.replace(/\s/g, ''),
    );

    this.superAdminService
      .makePremiumUsers(phones)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isSubmitting = false;
          this.result = res;
          // Reset form but keep the result card visible below
          this.premiumForm.reset();
          this.phoneNumbers.clear();
          this.phoneNumbers.push(this.createPhoneControl());
        },
        error: (err) => {
          this.isSubmitting = false;
          this.submitError =
            err?.error?.message || 'Something went wrong. Please try again.';
        },
      });
  }

  resetAll(): void {
    this.result = null;
    this.submitError = '';
    this.premiumForm.reset();
    this.phoneNumbers.clear();
    this.phoneNumbers.push(this.createPhoneControl());
  }

  // ─── Template Helpers ─────────────────────────────────────────────────────

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  avatarLetter(name: string): string {
    return name?.charAt(0)?.toUpperCase() ?? '?';
  }

  get allUpgraded(): boolean {
    return (
      !!this.result &&
      this.result.upgraded.length === this.result.summary.requested
    );
  }
}